/**
 * VH-126: the user's training places.
 *
 * Per the UI decision, the place is the home screen of this feature — you
 * choose where you are before anything else, and everything captured
 * afterwards belongs to it.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MapPinIcon, PlusIcon } from '@heroicons/react/24/outline'
import {
  useCreateLocation,
  useTrainingLocations,
} from '../hooks/useTrainingLocations'

export default function TrainingLocations() {
  const { t } = useTranslation()
  const { data, isLoading, error } = useTrainingLocations()
  const createLocation = useCreateLocation()

  const [name, setName] = useState('')
  const [formError, setFormError] = useState(null)

  const locations = data?.locations || []

  const handleCreate = async (event) => {
    event.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    setFormError(null)
    try {
      await createLocation.mutateAsync({ name: trimmed })
      setName('')
    } catch (err) {
      setFormError(
        err?.response?.data?.detail || t('trainingLocations.errors.createFailed')
      )
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="training-locations-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <MapPinIcon className="h-8 w-8 text-primary-600" />
          {t('trainingLocations.title')}
        </h1>
        <p className="mt-1 text-gray-600">{t('trainingLocations.subtitle')}</p>
      </div>

      <form onSubmit={handleCreate} className="mb-6 flex gap-2" data-testid="new-location-form">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('trainingLocations.namePlaceholder')}
          maxLength={120}
          className="input flex-1"
          aria-label={t('trainingLocations.nameAria')}
        />
        <button
          type="submit"
          className="btn-primary flex items-center gap-2"
          disabled={createLocation.isPending || !name.trim()}
          data-testid="create-location"
        >
          <PlusIcon className="h-4 w-4" />
          {t('trainingLocations.add')}
        </button>
      </form>

      {formError && (
        <p className="mb-4 text-sm text-red-600" role="alert" data-testid="create-error">
          {formError}
        </p>
      )}

      {isLoading && <p className="text-gray-600">{t('common.loading')}</p>}
      {error && (
        <p className="text-red-600" role="alert">
          {t('trainingLocations.errors.loadFailed')}
        </p>
      )}

      {!isLoading && !error && locations.length === 0 && (
        <div className="text-center py-12" data-testid="locations-empty">
          <p className="text-gray-600">{t('trainingLocations.empty')}</p>
        </div>
      )}

      {/*
        VH-131: the way to every machine at once. Kept deliberately — VH-130
        defaulted the location filter to ALL because a forced scope hides
        machines, and per-place browsing alone would reintroduce exactly that:
        a machine filed at the wrong place could only be found by opening each
        place in turn. Hidden when there are no places, since there can then be
        no equipment either (location_id is NOT NULL since 074).
      */}
      {locations.length > 0 && (
        <Link
          to="/equipment"
          className="mb-3 block rounded-lg border border-gray-200 bg-surface-container p-4 hover:border-primary-300"
          data-testid="all-equipment-link"
        >
          <div className="flex items-center justify-between gap-4">
            <span className="font-semibold text-gray-900">{t('equipment.allEquipment')}</span>
            <span className="text-sm text-gray-500">
              {t('trainingLocations.equipmentCount', {
                count: locations.reduce((sum, l) => sum + (l.equipment_count || 0), 0),
              })}
            </span>
          </div>
        </Link>
      )}

      <ul className="space-y-3">
        {locations.map((location) => (
          <li key={location.id}>
            <Link
              to={`/training-locations/${location.id}`}
              className="block rounded-lg border border-gray-200 bg-surface-container p-4 hover:border-primary-300"
              data-testid={`location-${location.id}`}
            >
              <div className="flex items-center justify-between gap-4">
                <span className="font-semibold text-gray-900">{location.name}</span>
                <span className="text-sm text-gray-500">
                  {t('trainingLocations.equipmentCount', {
                    count: location.equipment_count,
                  })}
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
